/**
 * Monte Carlo driver (PRD §4.6).
 *
 * The run is split into fixed-size BLOCKS over disjoint iteration ranges.
 * Because the PRNG is position-addressable, a block needs nothing from its
 * siblings: no stream state, no ordering, no locks.
 *
 * Blocks — not workers — are the unit of reduction, and that distinction is
 * what actually delivers PRD §6.2. Position-addressable sampling alone is not
 * enough: floating-point merging is not associative, so combining 8 partial
 * means gives a different last bit than combining 2, even when every sample is
 * identical. Deriving the block count from the iteration count alone, and
 * reducing blocks through a fixed pairwise tree, makes the arithmetic order a
 * property of the job rather than of the machine that ran it. Worker count then
 * genuinely cannot reach the result.
 *
 * This module is deliberately transport-agnostic. `runBlock` is the unit a Web
 * Worker will execute; `runMonteCarlo` drives blocks in-process so the whole
 * engine stays testable headlessly, exactly as the v0.1 milestone requires.
 */

import type { CompiledModel } from './model.ts';
import { Sampler } from './sampling.ts';
import { OutputAccumulator, RunAccumulator, type OutputStats } from './accumulator.ts';
import { nominal, variance as distVariance } from './distributions.ts';
import {
  wilsonYield,
  capability,
  normalityTest,
  spearman,
  choleskySolve,
  type YieldEstimate,
  type Capability,
  type NormalityResult,
} from './stats.ts';

export interface MonteCarloOptions {
  iterations: number;
  /** Defaults to the project's stored seed. */
  seed?: number;
  bins?: number;
  /**
   * How many blocks to hand out at a time. Purely a scheduling and progress
   * granularity knob — it cannot affect results, and the determinism tests
   * assert exactly that.
   */
  shards?: number;
  subsampleLimit?: number;
  /** Called after each shard; return false to stop early. */
  onProgress?: (completed: number, total: number) => boolean | void;
}

/**
 * Iterations per reduction block.
 *
 * Small enough that a block is a reasonable unit of work to hand a worker,
 * large enough that per-block overhead is negligible.
 */
const BLOCK_SIZE = 16_384;
const MAX_BLOCKS = 512;

/**
 * The block partition for a run. A pure function of `iterations`, which is the
 * property that makes the reduction reproducible.
 *
 * The count is rounded up to a power of two. That makes the pairwise reduction
 * a perfect binary tree, and a perfect binary tree is what allows a worker to
 * reduce a contiguous aligned chunk of blocks locally and still yield exactly
 * what the single-threaded run would have produced: any aligned power-of-two
 * run of blocks is a complete subtree, so combining subtree roots in order
 * reproduces the whole tree. Without this, parallel reduction would have to
 * ship every block back to the coordinator.
 */
export function blockPlan(iterations: number): { count: number; size: number } {
  const wanted = Math.max(1, Math.ceil(iterations / BLOCK_SIZE));
  let count = 1;
  while (count < wanted && count < MAX_BLOCKS) count <<= 1;
  return { count, size: Math.ceil(iterations / count) };
}

/**
 * Fixed-shape pairwise reduction.
 *
 * Accumulators are pushed in ascending block order and combined through a
 * canonical binary tree, so the sequence of floating-point merge operations
 * depends only on how many blocks there are. Holding O(log n) partials rather
 * than all n also keeps peak memory flat.
 */
export class PairwiseReducer {
  private readonly stack: { level: number; acc: RunAccumulator }[] = [];

  push(acc: RunAccumulator): void {
    let level = 0;
    let carry = acc;
    while (this.stack.length > 0 && this.stack[this.stack.length - 1].level === level) {
      const top = this.stack.pop()!;
      // Earlier range absorbs the later one, preserving block order.
      top.acc.merge(carry);
      carry = top.acc;
      level++;
    }
    this.stack.push({ level, acc: carry });
  }

  finish(): RunAccumulator | null {
    if (this.stack.length === 0) return null;
    const base = this.stack[0].acc;
    for (let i = 1; i < this.stack.length; i++) base.merge(this.stack[i].acc);
    return base;
  }
}

export interface HistogramData {
  lo: number;
  hi: number;
  binWidth: number;
  counts: Float64Array;
  underflow: number;
  overflow: number;
}

export interface Sensitivity {
  varId: string;
  /** Rank correlation, from the retained subsample. Robust to nonlinearity. */
  spearman: number;
  /** Standardized regression coefficient. Signed; interpretable as sigma-per-sigma. */
  src: number;
  /** SRC^2 normalised across variables — approximate share of output variance. */
  contribution: number;
}

export interface OutputResult {
  id: string;
  unit?: string;
  lsl?: number;
  usl?: number;
  target?: number;
  stats: OutputStats;
  histogram: HistogramData;
  percentile: (p: number) => number;
  /** Present only when the output has at least one spec limit. */
  yield?: YieldEstimate;
  capability: Capability;
  normality: NormalityResult;
  sensitivities: Sensitivity[];
  /**
   * Fraction of output variance explained by the linear model. SRC values are
   * only trustworthy for R^2 above roughly 0.7 (PRD §4.6); below that, read the
   * Spearman column instead.
   */
  regressionR2: number;
  /** True when the transfer function contains a non-smooth construct. */
  smooth: boolean;
}

export interface MonteCarloResult {
  iterations: number;
  completedIterations: number;
  seed: number;
  elapsedMs: number;
  outputs: OutputResult[];
  /** All limited outputs simultaneously in spec. Never the product of the individual yields. */
  systemYield?: YieldEstimate;
  subsampleSize: number;
  cancelled: boolean;
}

interface RangeSpec {
  lo: number;
  hi: number;
  bins: number;
}

/** Run one iteration range. This is the unit a Web Worker will own. */
export function runBlock(
  model: CompiledModel,
  seed: number,
  from: number,
  to: number,
  ranges: RangeSpec[],
  subsampleLimit: number,
): RunAccumulator {
  const nVars = model.varIds.length;
  const sampler = new Sampler(model, seed);
  const env = new Float64Array(model.envSize);
  const outs = new Float64Array(model.outputs.length);

  const centre = new Float64Array(nVars);
  for (let i = 0; i < nVars; i++) centre[i] = nominal(model.distributions[i]);

  const accs = model.outputs.map(
    (o, i) =>
      new OutputAccumulator(ranges[i].lo, ranges[i].hi, ranges[i].bins, o.spec.lsl, o.spec.usl),
  );

  // The merged subsample is a prefix of the run in block order, so a block
  // starting past the limit can never contribute to it. Skipping collection
  // there is not an approximation — those samples were always discarded — and
  // it removes the bulk of the memory and postMessage cost in a parallel run.
  const collect = from < subsampleLimit ? subsampleLimit : 0;

  const run = new RunAccumulator(accs, nVars, centre, collect);
  const vars = env.subarray(0, nVars);

  for (let it = from; it < to; it++) {
    sampler.sampleInto(env, it);
    model.evaluate(env, outs);
    run.push(vars, outs);
  }

  return run;
}

/**
 * Reduce a contiguous, power-of-two-aligned run of blocks into one accumulator.
 *
 * This is what a worker executes. The result is a complete subtree of the
 * canonical reduction tree, so the coordinator can combine chunk results in
 * chunk order and land on exactly the single-threaded answer.
 */
export function runChunk(
  model: CompiledModel,
  seed: number,
  firstBlock: number,
  blockCount: number,
  plan: { count: number; size: number },
  iterations: number,
  ranges: RangeSpec[],
  subsampleLimit: number,
  onBlock?: (blocksDone: number) => boolean | void,
): RunAccumulator | null {
  const reducer = new PairwiseReducer();
  for (let b = firstBlock; b < firstBlock + blockCount && b < plan.count; b++) {
    const from = Math.min(b * plan.size, iterations);
    const to = Math.min(iterations, from + plan.size);
    reducer.push(runBlock(model, seed, from, to, ranges, subsampleLimit));
    if (onBlock?.(b - firstBlock + 1) === false) break;
  }
  return reducer.finish();
}

/**
 * Pilot pass to choose histogram ranges.
 *
 * Only the histogram depends on this — yield comes from exact counters, so a
 * badly chosen range degrades the plot and the percentiles but can never
 * corrupt the numbers the report is signed off on.
 */
function pilotRanges(
  model: CompiledModel,
  seed: number,
  iterations: number,
  bins: number,
): RangeSpec[] {
  const pilotN = Math.max(1000, Math.min(10_000, Math.floor(iterations / 10)));
  const sampler = new Sampler(model, seed);
  const env = new Float64Array(model.envSize);
  const outs = new Float64Array(model.outputs.length);

  const n = model.outputs.length;
  const mins = new Float64Array(n).fill(Infinity);
  const maxs = new Float64Array(n).fill(-Infinity);
  const sums = new Float64Array(n);
  const sumsSq = new Float64Array(n);

  for (let it = 0; it < pilotN; it++) {
    sampler.sampleInto(env, it);
    model.evaluate(env, outs);
    for (let o = 0; o < n; o++) {
      const y = outs[o];
      if (y < mins[o]) mins[o] = y;
      if (y > maxs[o]) maxs[o] = y;
      sums[o] += y;
      sumsSq[o] += y * y;
    }
  }

  return model.outputs.map((out, o) => {
    const mean = sums[o] / pilotN;
    const varEst = Math.max(0, sumsSq[o] / pilotN - mean * mean);
    const sd = Math.sqrt(varEst);

    // Cover the pilot's observed range, plus 6 sigma either side so the full
    // run's tails mostly land inside, plus any spec limits so they are visible
    // on the plot rather than off its edge.
    let lo = Math.min(mins[o], mean - 6 * sd);
    let hi = Math.max(maxs[o], mean + 6 * sd);
    if (out.spec.lsl !== undefined) lo = Math.min(lo, out.spec.lsl);
    if (out.spec.usl !== undefined) hi = Math.max(hi, out.spec.usl);

    if (!(hi > lo)) {
      // Degenerate: a constant output. Give the histogram a nominal width so
      // bin arithmetic stays finite.
      const eps = Math.abs(lo) > 0 ? Math.abs(lo) * 1e-9 : 1e-9;
      lo -= eps;
      hi += eps;
    } else {
      const pad = (hi - lo) * 0.05;
      lo -= pad;
      hi += pad;
    }

    return { lo, hi, bins };
  });
}

function computeSensitivities(
  model: CompiledModel,
  run: RunAccumulator,
  outputIndex: number,
): { sensitivities: Sensitivity[]; r2: number } {
  const nVars = model.varIds.length;
  const nOuts = model.outputs.length;

  // Fixed and otherwise degenerate variables carry no variance and would make
  // the normal equations singular. Drop them here and report zero for them.
  const active: number[] = [];
  for (let i = 0; i < nVars; i++) {
    if (distVariance(model.distributions[i]) > 0 && run.covXX(i, i) > 0) active.push(i);
  }

  const varY = run.varY(outputIndex);
  const sensitivities: Sensitivity[] = model.varIds.map((varId) => ({
    varId,
    spearman: 0,
    src: 0,
    contribution: 0,
  }));

  // Spearman from the retained subsample.
  const m = run.subsampleCount;
  if (m > 2) {
    const col = new Float64Array(m);
    const yCol = new Float64Array(m);
    for (let k = 0; k < m; k++) yCol[k] = run.subY[k * nOuts + outputIndex];
    for (const i of active) {
      for (let k = 0; k < m; k++) col[k] = run.subX[k * nVars + i];
      sensitivities[i].spearman = spearman(col, yCol, m);
    }
  }

  if (active.length === 0 || !(varY > 0)) {
    return { sensitivities, r2: 0 };
  }

  // Standardized regression coefficients from the streamed covariance matrix.
  const k = active.length;
  const a = new Float64Array(k * k);
  const b = new Float64Array(k);
  for (let p = 0; p < k; p++) {
    for (let q = 0; q < k; q++) a[p * k + q] = run.covXX(active[p], active[q]);
    b[p] = run.covXY(active[p], outputIndex);
  }

  const beta = choleskySolve(a, b, k);
  if (!beta) return { sensitivities, r2: 0 };

  let r2 = 0;
  let sumSq = 0;
  for (let p = 0; p < k; p++) {
    const i = active[p];
    const sdX = Math.sqrt(run.covXX(i, i));
    const src = (beta[p] * sdX) / Math.sqrt(varY);
    sensitivities[i].src = src;
    sumSq += src * src;
    r2 += (beta[p] * b[p]) / varY;
  }
  if (sumSq > 0) {
    for (const i of active) {
      sensitivities[i].contribution = (sensitivities[i].src * sensitivities[i].src) / sumSq;
    }
  }

  return { sensitivities, r2: Math.max(0, Math.min(1, r2)) };
}

export function runMonteCarlo(
  model: CompiledModel,
  options: MonteCarloOptions,
): MonteCarloResult {
  const iterations = Math.max(1, Math.floor(options.iterations));
  const seed = options.seed ?? model.project.seed;
  const bins = options.bins ?? 4096;
  const shards = Math.max(1, Math.floor(options.shards ?? 1));
  const subsampleLimit = options.subsampleLimit ?? 65536;

  const started = Date.now();
  const ranges = pilotRanges(model, seed, iterations, bins);

  // The partition depends only on `iterations`; `shards` merely decides how
  // often progress is reported and, in the worker build, who runs what.
  const plan = blockPlan(iterations);
  const reducer = new PairwiseReducer();
  const reportEvery = Math.max(1, Math.ceil(plan.count / shards));

  let completed = 0;
  let cancelled = false;

  for (let b = 0; b < plan.count; b++) {
    // Every block is pushed, including any empty tail block. Skipping empties
    // would change how many nodes the reduction tree has and therefore change
    // the floating-point result — the tree's shape must depend only on
    // `plan.count`.
    const from = Math.min(b * plan.size, iterations);
    const to = Math.min(iterations, from + plan.size);

    reducer.push(runBlock(model, seed, from, to, ranges, subsampleLimit));
    completed = to;

    if ((b + 1) % reportEvery === 0 || b === plan.count - 1) {
      if (options.onProgress?.(completed, iterations) === false) {
        cancelled = true;
        break;
      }
    }
  }

  const run = reducer.finish();
  if (run === null) throw new Error('No iterations were run');
  return assembleResult(model, run, iterations, seed, started, cancelled);
}

/**
 * Turn a reduced accumulator into the reported result.
 *
 * Shared by the in-process and Worker paths so that parallel runs cannot drift
 * from single-threaded ones in the reporting layer either.
 */
export function assembleResult(
  model: CompiledModel,
  run: RunAccumulator,
  iterations: number,
  seed: number,
  startedAt: number,
  cancelled: boolean,
): MonteCarloResult {

  const outputs: OutputResult[] = model.outputs.map((out, o) => {
    const acc = run.outputs[o];
    const stats = acc.stats();
    const { lsl, usl } = out.spec;
    const hasLimits = lsl !== undefined || usl !== undefined;
    const { sensitivities, r2 } = computeSensitivities(model, run, o);

    // Rank by contribution so the Pareto is ready to render.
    sensitivities.sort((x, y) => Math.abs(y.contribution) - Math.abs(x.contribution));

    return {
      id: out.spec.id,
      unit: out.spec.unit,
      lsl,
      usl,
      target: out.spec.target,
      stats,
      histogram: {
        lo: acc.lo,
        hi: acc.hi,
        binWidth: acc.binWidth,
        counts: acc.bins,
        underflow: acc.underflow,
        overflow: acc.overflow,
      },
      percentile: (p: number) => acc.percentile(p),
      yield: hasLimits ? wilsonYield(stats.inSpec, stats.count) : undefined,
      capability: capability(stats.mean, stats.stdDev, lsl, usl),
      normality: normalityTest(stats.count, stats.skewness, stats.kurtosis),
      sensitivities,
      regressionR2: r2,
      smooth: out.smooth,
    };
  });

  const anyLimits = model.outputs.some(
    (o) => o.spec.lsl !== undefined || o.spec.usl !== undefined,
  );

  return {
    iterations,
    completedIterations: run.total,
    seed,
    elapsedMs: Date.now() - startedAt,
    outputs,
    systemYield: anyLimits ? wilsonYield(run.systemPass, run.total) : undefined,
    subsampleSize: run.subsampleCount,
    cancelled,
  };
}

/** Exposed so the Worker coordinator can compute ranges once and share them. */
export { pilotRanges, type RangeSpec };
